import { DestroyRef, Injectable, Signal, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { QueryClient } from '@tanstack/angular-query-experimental';

import { NotificationService } from '@core/shared/util';
import { MaintenanceSettings } from '@mp/system/maintenance/domain';

import { maintenanceStateQueryKey } from '../query';
import { MaintenanceSettingsService } from '../services';

@Injectable()
export class MaintenanceSettingsFacade {
  private readonly maintenanceSettingsService = inject(MaintenanceSettingsService);
  private readonly notificationService = inject(NotificationService);
  private readonly queryClient = inject(QueryClient);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _settings = signal<MaintenanceSettings | null>(null);
  private readonly _isLoading = signal<boolean>(false);
  private readonly _isSaving = signal<boolean>(false);

  /**
   * The configured maintenance window, `null` as long as it has not been loaded.
   */
  readonly settings: Signal<MaintenanceSettings | null> = this._settings.asReadonly();

  /**
   * Whether the maintenance window is currently being loaded.
   */
  readonly isLoading: Signal<boolean> = this._isLoading.asReadonly();

  /**
   * Whether the maintenance window is currently being saved.
   */
  readonly isSaving: Signal<boolean> = this._isSaving.asReadonly();

  /**
   * Loads the configured maintenance window.
   */
  load(): void {
    this._isLoading.set(true);

    this.maintenanceSettingsService
      .getMaintenanceSettings()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (settings) => {
          this._settings.set(settings);
          this._isLoading.set(false);
        },
        error: () => {
          this._isLoading.set(false);
          this.notificationService.toastDanger('Das Wartungsfenster konnte nicht geladen werden.');
        },
      });
  }

  /**
   * Saves the maintenance window.
   * @param settings The settings to save.
   */
  save(settings: MaintenanceSettings): void {
    this._isSaving.set(true);

    this.maintenanceSettingsService
      .updateMaintenanceSettings(settings)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (savedSettings) => {
          this._settings.set(savedSettings);
          this._isSaving.set(false);
          this.notificationService.toastSuccess('Das Wartungsfenster wurde gespeichert.');

          /**
           * Der Wartungszustand aller offenen Oberflächen hängt am gespeicherten Fenster - ohne die
           * Invalidierung sähe der speichernde Administrator seine Änderung erst beim nächsten Poll.
           */
          void this.queryClient.invalidateQueries({ queryKey: maintenanceStateQueryKey });
        },
        error: () => {
          this._isSaving.set(false);
          this.notificationService.toastDanger('Das Wartungsfenster konnte nicht gespeichert werden.');
        },
      });
  }
}
